import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { initializeBuckets } from './lib/supabase';
import { useAuth } from './contexts/AuthContext';
import { RefreshProvider } from './contexts/RefreshContext';
import { registerServiceWorker } from './lib/pushNotificationService';

// Layouts and Components
import Navbar from './components/Navbar';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';

// Context Providers
import { AuthProvider } from './contexts/AuthContext';
import { PostProvider } from './contexts/PostContext';
import { ToastProvider } from './contexts/ToastContext';

// Lazy-loaded Pages
const Home = lazy(() => import('./pages/Home'));
const Feed = lazy(() => import('./pages/Feed'));
const Explore = lazy(() => import('./pages/Explore'));
const Profile = lazy(() => import('./pages/Profile'));
const Post = lazy(() => import('./pages/Post'));
const SignIn = lazy(() => import('./pages/SignIn'));
const SignUp = lazy(() => import('./pages/SignUp'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Admin = lazy(() => import('./pages/Admin'));
const DatabaseStatus = lazy(() => import('./pages/DatabaseStatus'));
const EditProfile = lazy(() => import('./pages/EditProfile'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Help = lazy(() => import('./pages/Help'));
const Termos = lazy(() => import('./pages/Termos'));
const Privacidade = lazy(() => import('./pages/Privacidade'));
const ServerError = lazy(() => import('./pages/ServerError'));
const Forbidden = lazy(() => import('./pages/Forbidden'));
const Maintenance = lazy(() => import('./pages/Maintenance'));

// Lazy-loaded Components
const CreatePost = lazy(() => import('./components/CreatePost'));

// Componente de fallback global
const GlobalLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-[calc(100vh-8rem)] mt-20 sm:mt-24">
    <LoadingSpinner size="large" />
  </div>
);

// Componente de erro global
const GlobalErrorFallback = ({ error }: { error: string }) => (
  <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] p-4 text-center">
    <div className="bg-red-50 rounded-lg p-6 max-w-md">
      <h2 className="text-red-600 text-xl font-semibold mb-3">Ops! Algo deu errado</h2>
      <p className="text-gray-600">{error}</p>
      <button 
        onClick={() => window.location.reload()}
        className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
      >
        Tentar novamente
      </button>
    </div>
  </div>
);

// Inner component that uses auth context
function AppContent() {
  const navigate = useNavigate();
  const { signOut } = useAuth();

  useEffect(() => {
    const initialize = async () => {
      try {
        await initializeBuckets();
        // Register service worker for push notifications
        await registerServiceWorker();
      } catch (error) {
        console.error('Erro ao inicializar aplicação:', error);
      }
    };
    initialize();
  }, []);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    const handleActivity = () => {
      clearTimeout(timeout);
      timeout = setTimeout(async () => {
        try {
          await signOut();
          navigate('/entrar');
        } catch (error) {
          console.error('Erro ao fazer logout:', error);
        }
      }, 5 * 60 * 1000); // 5 minutos
    };

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keypress', handleActivity);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keypress', handleActivity);
    };
  }, [signOut, navigate]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />
      <Suspense fallback={<GlobalLoadingFallback />}>
        <main className="flex-grow mt-12 sm:mt-16">
          <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/feed" element={<Feed />} />
              <Route path="/explorar" element={<Explore />} />
              <Route path="/perfil/:username" element={<Profile />} />
              <Route path="/perfil/editar" element={<EditProfile />} />
              <Route path="/post/:id" element={<Post />} />
              <Route path="/entrar" element={<SignIn />} />
              <Route path="/cadastro" element={<SignUp />} />
              <Route path="/esqueceu-senha" element={<ForgotPassword />} />
              <Route path="/redefinir-senha" element={<ResetPassword />} />
              <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
              <Route path="/database-status" element={<DatabaseStatus />} />
              <Route path="/ajuda" element={<Help />} />
              <Route path="/termos" element={<Termos />} />
              <Route path="/privacidade" element={<Privacidade />} />
              <Route path="/404" element={<NotFound />} />
              <Route path="/403" element={<Forbidden />} />
              <Route path="/500" element={<ServerError />} />
              <Route path="/manutencao" element={<Maintenance />} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
          </div>
        </main>
      </Suspense>
      <Footer />
      <Toaster 
        position="bottom-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
            fontSize: '0.875rem',
            fontFamily: 'Poppins, sans-serif',
            padding: '0.75rem 1rem',
            borderRadius: '0.5rem',
          },
        }}
      />
    </div>
  );
}

function App() {

  return (
    <ErrorBoundary fallbackRender={({ error }) => <GlobalErrorFallback error={error.message} />}>
      <AuthProvider>
        <ToastProvider>
          <RefreshProvider>
            <PostProvider>
              <AppContent />
            </PostProvider>
          </RefreshProvider>
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;